import {ConfigObjectType, TranslateUtils, enumContainer} from '@wallaby-ui/core';
import {useSnomedLabels} from '@wallaby-ui/document';
import {components} from '@wallaby-ui/document/types/js/common/_generated/haidi_schema';
import {useIntl} from 'react-intl';
import {useAppSelector} from 'src/js/store_hooks';
import {translateHaiRuleTooltip} from './haiRuleTooltip';

// Same parameter type TranslateUtils.translate accepts, so the two never drift apart.
type TextParams = Parameters<typeof TranslateUtils.translate>[2];
type TFunc = (key: string, textParams?: TextParams) => string;
type EnumMapT = Record<string, any[]>;
function getHaiTypeTranslation(t: TFunc, enumMap: EnumMapT) {
  return (type: components['schemas']['HaiTypeEnum']) => t(enumMap.HaiTypeEnum.find((a) => a.name === type)?.messageKey);
}

function getHaiStateTranslation(t: TFunc, enumMap: EnumMapT) {
  return (type: components['schemas']['State']) => t(enumMap.State.find((a) => a.name === type)?.messageKey);
}

function getHaiOriginTranslation(t: TFunc, enumMap: EnumMapT) {
  return (origin: components['schemas']['Origin']) => t(enumMap.Origin.find((a) => a.name === origin)?.messageKey);
}

function getHaiBsiOriginTranslation(t: TFunc, enumMap: EnumMapT) {
  return (origin: components['schemas']['BSIOrigin'] | undefined) => t(enumMap.BSIOrigin.find((a) => a.name === origin)?.messageKey);
}

function getHaiPnOriginTranslation(t: TFunc, enumMap: EnumMapT) {
  return (origin: components['schemas']['PNOrigin'] | undefined) => t(enumMap.PNOrigin.find((a) => a.name === origin)?.messageKey);
}

function getHaiRulesTranslation(t: TFunc, enumMap: EnumMapT) {
  return (rules: components['schemas']['Rule'][]) => rules.map((r) => t(enumMap.Rule.find((a) => a.name === r)?.messageKey));
}

function getHaiRuleTranslation(t: TFunc, enumMap: EnumMapT) {
  return (rule: components['schemas']['Rule']) => t(enumMap.Rule.find((a) => a.name === rule)?.messageKey);
}

// PDH-3259: see translateHaiRuleTooltip for the `{days}` placeholder of the outpatient SSI tooltip.
function getHaiRuleTooltipTranslation(t: TFunc, enumMap: EnumMapT) {
  return (ruleTooltip: components['schemas']['RuleTooltip'], ssiPeriodDays?: number | null) =>
    translateHaiRuleTooltip(t, enumMap.RuleTooltip.find((a) => a.name === ruleTooltip)?.messageKey, ruleTooltip, ssiPeriodDays);
}

function getHaiReporterTranslation(t: TFunc, enumMap: EnumMapT) {
  return (reporters: components['schemas']['Reporter'][]) =>
    reporters.map((r) => t(enumMap.Reporter.find((a) => a.name === r)?.messageKey));
}

function getHaiDeathTranslation(t: TFunc, enumMap: EnumMapT) {
  return (death: components['schemas']['HaiDeathEnum']) => t(enumMap.HaiDeathEnum.find((a) => a.name === death)?.messageKey);
}

function getHaiPathogensTranslation(snomedLabels: Record<string, string>) {
  return (pathogens: components['schemas']['HaiPathogenDto'][]) => pathogens.map((a) => snomedLabels[a.code]).join(', ');
}

function getHaiDefinitionsTranslation(t: TFunc, enumMap: EnumMapT) {
  return (definitions: components['schemas']['Definition'][]) =>
    definitions.map((d) => t(enumMap.Definition.find((a) => a.name === d)?.messageKey));
}

export function useAllTranslations(config: ConfigObjectType) {
  const intl = useIntl();
  const t: TFunc = (key, textParams) => TranslateUtils.translate(key, intl, textParams);
  const enumMap = useAppSelector((state) => (state[enumContainer.ENUM_REDUCER_STATE_ID] as any).enumMap);

  const typeTranslation = getHaiTypeTranslation(t, enumMap);
  const stateTranslation = getHaiStateTranslation(t, enumMap);
  const originTranslation = getHaiOriginTranslation(t, enumMap);
  const bsiOriginTranslation = getHaiBsiOriginTranslation(t, enumMap);
  const pnOriginTranslation = getHaiPnOriginTranslation(t, enumMap);
  const rulesTranslation = getHaiRulesTranslation(t, enumMap);
  const ruleTranslation = getHaiRuleTranslation(t, enumMap);
  const ruleTooltipsTranslation = getHaiRuleTooltipTranslation(t, enumMap);
  const reporterTranslation = getHaiReporterTranslation(t, enumMap);
  const deathTranslation = getHaiDeathTranslation(t, enumMap);
  const {snomedLabels, snomedLabelsLoading} = useSnomedLabels(config);
  const pathogensTranslation = getHaiPathogensTranslation(snomedLabels);
  const definitionsTranslation = getHaiDefinitionsTranslation(t, enumMap);

  return {
    typeTranslation,
    stateTranslation,
    originTranslation,
    bsiOriginTranslation,
    pnOriginTranslation,
    rulesTranslation,
    ruleTranslation,
    ruleTooltipsTranslation,
    reporterTranslation,
    deathTranslation,
    pathogensTranslation,
    definitionsTranslation,
    snomedLabelsLoading,
  };
}
